import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ISerializeObject, RequestService } from '@df/api/request/request.service';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { Listing, LISTING_UPDATE_VISIBLE_MODELS } from '@df/catalog-ui/listing/listing';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfEventService, IEventType } from '@df/core/service/df-event';
import { Url } from '@df/core/service/url';
import { PRODUCTS_SEARCH_ID } from '@df/module-klevu-search/klevu-search.service';
import { TimeoutService } from '@df/ng/timeout.service';
import chunk from 'lodash-es/chunk';
import mapKeys from 'lodash-es/mapKeys';
import pick from 'lodash-es/pick';
import take from 'lodash-es/take';
import { BehaviorSubject } from 'rxjs';
import { KlevuSearch } from './klevu-interfaces';

@Injectable({
    providedIn: 'root'
})
export class KlevuTrackService {
    private configurations: { [key: string]: KlevuSearch.IRecommendationsConfiguration } = {};

    @LocalStorage('klevuab')
    private abDataStorage?: KlevuSearch.IABStorage;

    private abData?: KlevuSearch.IABStorage;
    abDataLoaded = new BehaviorSubject<boolean>(false);

    private requestService = inject(RequestService);
    private timeoutService = inject(TimeoutService);
    private app = inject(App);
    private http = inject(HttpClient);
    private dfEventService = inject(DfEventService);
    private url = inject(Url);

    appInitialize() {
        this.abData = this.abDataStorage;
        this.checkABData();

        this.dfEventService.success(event => {
            if (event.id === 'nexusCheckoutSuccess' && event.result!.success.tracking) {
                this.trackOrder(event.result!);
            }
        });

        this.setCategoryProductViewTracking();
    }

    get config(): KlevuSearch.IConfig {
        return this.app.data?.klevu || ({} as KlevuSearch.IConfig);
    }

    get apiKey(): string | undefined {
        return this.config?.apiKey;
    }

    /**
     * Retrives category path for sort order request
     */
    protected getCategoryPath(category: Category<ICategoryDataV3>): string {
        return category.data?.breadcrumbs?.map(i => i.label).join(';') || '';
    }

    protected track(endpoint: KlevuSearch.ANALYTICS_ENDPOINTS, data: ISerializeObject): void {
        this.http
            .get(`${endpoint}?${this.requestService.serialize(data)}`)
            .toPromise()
            .catch(() => {});
    }

    getSimpleId(product: Product<IProductDataV2>): string {
        if (product.isConfigurable) {
            return product.childrenIds?.[0]?.toString() || product.id.toString();
        }
        return product.id.toString();
    }

    getProductId(product: Product<IProductDataV2>): string {
        if (product.isConfigurable) {
            const firstChild = product.childrenIds?.[0];
            if (firstChild) {
                return `${product.id}-${firstChild}`;
            }
        }
        const parentId = product.data?.parent;
        if (parentId && parentId !== product.id) {
            return `${parentId}-${product.id}`;
        }
        return product.id.toString();
    }

    ////////////////////////////////////////////
    ////////
    ////////    Smart Search
    ////////
    ////////////////////////////////////////////

    /**
     * Search result tracking
     */
    trackSearchResult(response: KlevuSearch.ISearchResponse): void {
        const productsResult = response.queryResults?.find(i => i.id === PRODUCTS_SEARCH_ID);

        if (productsResult) {
            this.track(KlevuSearch.ANALYTICS_ENDPOINTS.search, this.getTrackSearchTermRequest(productsResult) as any as ISerializeObject);
        }
    }

    trackBanner(banner: KlevuSearch.IBanner, term: string): void {
        const trackData = this.getBannerTrackData(banner, term) as any as ISerializeObject;
        this.track(KlevuSearch.ANALYTICS_ENDPOINTS.bannerTrack, trackData);
    }

    private getBannerTrackData(data: KlevuSearch.IBanner, term: string) {
        return {
            klevu_apiKey: this.apiKey,
            klevu_bannerId: data.bannerRef,
            klevu_bannerName: data.bannerName,
            klevu_request: 'click',
            type: 'banner',
            klevu_image: data.bannerImg,
            klevu_term: term,
            klevu_ip: '',
            klevu_target: ''
        };
    }

    private getTrackSearchTermRequest(result: KlevuSearch.ISearchResponseItem): KlevuSearch.IAnalyticsSearchedTermRequest {
        const meta = result.meta;
        return {
            klevu_apiKey: meta.apiKey,
            klevu_term: meta.searchedTerm,
            klevu_totalResults: meta.totalResultsFound,
            klevu_typeOfQuery: meta.typeOfSearch
        };
    }

    /**
     * Sends tracking request for product click
     * @param term search term
     * @param product Clicked product
     */
    trackSearchResultProductClick(term: string, product: Product<IProductDataV2>) {
        const trackData = this.getSearchResultProductTrackData(term, product) as any as ISerializeObject;
        this.track(KlevuSearch.ANALYTICS_ENDPOINTS.searchProduct, trackData);
    }

    private getSearchResultProductTrackData(term: string, product: Product<IProductDataV2>): KlevuSearch.IAnalyticsClickedProductRequest {
        return {
            klevu_apiKey: this.apiKey!,
            klevu_keywords: term,
            klevu_productId: this.getProductId(product),
            klevu_productGroupId: product.parent.id.toString() || product.id.toString(),
            klevu_productVariantId: this.getSimpleId(product),
            klevu_productName: product.name,
            klevu_productUrl: this.url.toUrl(product.data!.url!),
            klevu_type: 'clicked'
        };
    }

    private async trackOrder(response: IEventType['nexusCheckoutSuccess']['result']): Promise<void> {
        const successData = response?.success;

        if (successData?.tracking) {
            const items = successData.items || [];
            await this.app.resources.product.loadAllIds(
                items.map(p => p.product_id),
                2,
                { safe: true }
            );
            items.forEach(item => {
                const product = this.app.resources.product.getExisting(item.product_id) as Product<IProductDataV2>;
                const data: KlevuSearch.IAnalyticsSoldProductRequest = {
                    klevu_apiKey: this.apiKey!,
                    klevu_productId: this.getProductId(product),
                    klevu_productGroupId: product.parent.id.toString() || product.id.toString(),
                    klevu_productVariantId: this.getSimpleId(product),
                    klevu_type: 'checkout',
                    klevu_currency: successData.currency!,
                    klevu_salePrice: item.price,
                    klevu_unit: item.qty_ordered
                };
                this.track(KlevuSearch.ANALYTICS_ENDPOINTS.searchProduct, data as any as ISerializeObject);
            });
        }
    }

    ////////////////////////////////////////////
    ////////
    ////////    Smart Category Merchandising
    ////////
    ////////////////////////////////////////////

    private setCategoryProductViewTracking() {
        let lastCategoryId: number | undefined = undefined;
        let lastProducts: number[] = [];
        this.app.events.on(LISTING_UPDATE_VISIBLE_MODELS).subscribe((listing: Listing) => {
            const source = listing.config.source as Category<ICategoryDataV3>;
            if (typeof source === 'object' && source.type === Category.type) {
                const products = listing.models as Product<IProductDataV2>[];
                if (source.id !== lastCategoryId) {
                    lastCategoryId = source.id;
                    lastProducts = [];
                }
                const newProducts = products.filter(p => !lastProducts.includes(p.id));
                newProducts.forEach(product => lastProducts.push(product.id));

                if (newProducts.length) {
                    chunk(newProducts, 100).forEach(chunkedProducts => {
                        const trackData = this.getCategoryPageViewTrackData(source, chunkedProducts) as any as ISerializeObject;
                        this.track(KlevuSearch.ANALYTICS_ENDPOINTS.categoryProductView, trackData);
                    });
                }
            }
        });
    }

    private getCategoryPageViewTrackData(
        category: Category<ICategoryDataV3>,
        products: Product<IProductDataV2>[]
    ): KlevuSearch.IAnalyticsCategoryPageViewRequest {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const klevu_categoryPath = this.getCategoryPath(category);
        return {
            klevu_apiKey: this.apiKey!,
            klevu_categoryName: category.data!.name,
            klevu_categoryPath: this.getCategoryPath(category),
            klevu_productIds: products.map(product => this.getProductId(product)).join(','),
            ...mapKeys(this.getAbParams(KlevuSearch.catNavSearchType, klevu_categoryPath), (v, k) => 'klevu_' + k)
        };
    }

    trackCategoryProductClick(trackData: KlevuSearch.ITrackCategoryProductClickData): void {
        const data = this.getTrackCategoryProductClickRequest(trackData) as any as ISerializeObject;
        if (data) {
            this.track(KlevuSearch.ANALYTICS_ENDPOINTS.categoryProductClick, data);
        }
    }

    private getTrackCategoryProductClickRequest(
        trackData: KlevuSearch.ITrackCategoryProductClickData
    ): KlevuSearch.IAnalyticsCategoryProductClickRequest | undefined {
        const { category, product } = trackData;
        if (category && product) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const klevu_categoryPath = this.getCategoryPath(category);
            return {
                klevu_apiKey: this.apiKey!,
                klevu_categoryName: category.data!.name,
                klevu_categoryPath,
                klevu_productId: this.getProductId(product),
                klevu_productGroupId: product.parent.id.toString() || product.id.toString(),
                klevu_productVariantId: this.getSimpleId(product),
                klevu_productName: product.data?.name,
                klevu_productUrl: this.url.toUrl(product.data!.url!),
                klevu_productSku: product.data?.sku,
                klevu_salePrice: product.price.toString(),
                klevu_productPosition: trackData.position,
                ...mapKeys(this.getAbParams(KlevuSearch.catNavSearchType, klevu_categoryPath), (v, k) => 'klevu_' + k)
            };
        }
        return undefined;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Smart Recommendations
    ////////
    ////////////////////////////////////////////

    trackRecommendation<T extends KlevuSearch.EAnalyticsRecommendationEvent>(
        trackData: KlevuSearch.IAnalyticsRecommendationTrackData<T>
    ): void {
        this.loadRecommendationsConfig(trackData.eventListId)
            .then(configuration => {
                const payload = this.getRecommendationTrackRequest(trackData, configuration);
                if (payload) {
                    this.http
                        .post(KlevuSearch.ANALYTICS_ENDPOINTS.recommendationsCollect, [payload])
                        .toPromise()
                        .catch(() => {});
                }
            })
            .catch(() => {});
    }

    private getRecommendationTrackRequest<T extends KlevuSearch.EAnalyticsRecommendationEvent>(
        trackData: KlevuSearch.IAnalyticsRecommendationTrackData<T>,
        configuration: KlevuSearch.IRecommendationsConfiguration
    ): KlevuSearch.IAnalyticsRecommendationTrackRequest<T> | undefined {
        const items = trackData.products
            .map(i => this.getRecommendationProductItem(i.model, i.position))
            .filter(i => i.item_name && typeof i.price === 'number');
        if (items?.length) {
            return {
                event: trackData.event,
                event_apikey: this.apiKey!,
                event_list_id: trackData.eventListId,
                event_list_name: configuration.metadata.title,
                event_list_logic: configuration.metadata.logic,
                event_tags: ['recsPageType:' + configuration.metadata.pageType],
                items
            };
        }
        return undefined;
    }

    private getRecommendationProductItem(product: Product<IProductDataV2>, index: number): KlevuSearch.IAnalyticsRecommendationProductItem {
        const breadcrumbs = product.data?.breadcrumbs || [];
        const itemCategory =
            breadcrumbs.length > 1
                ? take(breadcrumbs, breadcrumbs.length - 1)
                      .map(i => i.label)
                      .join(';')
                : undefined;
        return {
            item_id: this.getProductId(product),
            item_group_id: product.parent.id.toString(),
            item_variant_id: product.id.toString(),
            item_name: product.data!.name!,
            price: product.price,
            currency: this.app.data.currency?.base,
            item_category: itemCategory,
            index
        };
    }

    loadRecommendationsConfig(
        id: string,
        params?: { pid?: string; gpid?: number; cp?: string }
    ): Promise<KlevuSearch.IRecommendationsConfiguration> {
        return new Promise((resolve, reject) => {
            const paramsString = params ? '?' + this.requestService.serialize(params) : '';
            const cacheId = `${id}.${paramsString}`;
            const cache = this.configurations[cacheId];
            if (id && cache) {
                resolve(cache);
            } else if (id && this.apiKey) {
                this.http
                    .get<KlevuSearch.IRecommendationsConfiguration>(
                        `https://config-cdn.ksearchnet.com/recommendations/${this.apiKey}/settings/${id}${paramsString}`
                    )
                    .toPromise()
                    .then(configuration => {
                        this.configurations[cacheId] = configuration!;
                        resolve(configuration!);
                    })
                    .catch(reject);
            } else {
                reject();
            }
        });
    }

    ////////////////////////////////////////////
    ////////
    ////////    AB TESTING
    ////////
    ////////////////////////////////////////////

    private checkABData(): void {
        if (this.app.isPlatformBrowser) {
            if (!((this.abData?.expires || 0) > Date.now())) {
                this.updateABData();
            } else {
                this.abDataLoaded.next(true);
            }

            this.timeoutService.setTimeout(() => this.checkABData(), 60 * 1000);
        }
    }

    private async updateABData() {
        const data = await this.loadABData();

        if (data) {
            data.forEach(entry => {
                if (typeof entry.sourceId === 'string') {
                    entry.sourceId = entry.sourceId.toLowerCase();
                }
            });
        }

        const storageEntry: KlevuSearch.IABStorage = {
            data,
            expires: Date.now() + 10 * 60 * 1000
        };

        this.abData = this.abDataStorage = storageEntry;
        this.abDataLoaded.next(true);
    }

    private async loadABData(): Promise<KlevuSearch.IABEntry[] | undefined> {
        const key = this.config.apiKey;

        if (!key || !this.app.isPlatformBrowser) {
            return undefined;
        }

        try {
            const url = `https://api.ksearchnet.com/abtest/public/allocation/${key}`;
            const res = (await this.http.post(url, {}).toPromise()) as KlevuSearch.IABResponse;

            return res.assigned;
        } catch (err) {
            this.app.logger.error('Klevu allocation load failed', err);
        }

        return undefined;
    }

    async trackCategoryOrder(category: Category<ICategoryDataV3>) {
        const key = this.apiKey;
        if (!key) {
            return undefined;
        }
        const categoryPath = this.getCategoryPath(category);
        const abEntry = this.getAbParams(KlevuSearch.catNavSearchType, categoryPath);
        if (abEntry.abTestId) {
            try {
                const url = `https://config-cdn.ksearchnet.com/abtest/public/usage/${key}`;
                const res = await this.http
                    .post(url, {
                        sourceId: categoryPath,
                        ...abEntry,
                        type: 'CAT_NAV'
                    })
                    .toPromise();
                return res;
            } catch (err) {
                this.app.logger.log('Klevu ab usage track failed', err);
            }
        }
    }

    getAbParams(type: string, categoryPath: string): { abTestId?: string; abTestVariantId?: string } {
        categoryPath = categoryPath.toLowerCase();
        const entry = this.abData?.data?.find(i => i.type === type && i.sourceId === categoryPath);
        return entry ? pick(entry, 'abTestId', 'abTestVariantId') : {};
    }
}
